import React, { Component } from "react";
import { Card, Tag, Typography, Space, Empty, Spin, Button, Progress, Alert } from "antd";
import { PmsAPI } from "../../../controller/PmsAPI";
import { cardState, isStale, severityTag, sourceLabel } from "./state";
import FeedbackItemsDrawer from "./FeedbackItemsDrawer";
import GuestNeedsSection from "./GuestNeedsSection";
import { FONT } from "../../styles/typography";

// 亮點 / 問題 / 建議行動三個區塊共用同一條標題樣式 —— 它們是同一層級的內容，
// 標題長得不一樣會讓人以為建議行動是附註而不是主要區塊。
const sectionHeaderStyle = {
  fontSize: FONT.body, fontWeight: 600, color: "#595959",
  marginBottom: 6, paddingBottom: 3, borderBottom: "1px solid #f0f0f0",
};

// 中文一行超過 45 字左右就會開始掉行 (回視、找不到下一行行首)。實測線上站 2026
// 年度的 overview 是 655 字、佔滿 1184px、一行約 85 個中文字 —— 卡片全寬不是
// 內文該有的寬度。亮點/問題本來就被 Row/Col 夾成約 580px 才好讀，這裡補上同一
// 個限制。
//
// 這個上限本身是對的，但它讓概述只佔左邊 560px、右邊約 620px 空著。解法不是把字
// 放寬，而是把右半拿來放建議行動 —— 它是整張卡最該被執行的內容，擺在第一屏比擺在
// 最下面合理。
//
// 但兩者不能綁進同一組百分比網格。第一版用 Col lg={12} 對半切，Playwright 實測只有
// 在約 1440px 才剛好：1920px 時欄寬 840，概述封頂 560 後欄內又空 280，同時建議行動
// 被拉到 824px、約 58 個字一行；1280px 時概述掉到 504px 變 5 行，1024px 只剩 376px、
// 6 行 —— 比不改還難讀。PROSE_MAX_WIDTH 是上限沒有下限，欄位會從下面把它切穿。
// 改用 flex 讓兩塊各自帶寬度需求 (下限保證不被壓扁、上限保證可讀)，放不下就換行堆疊。
// flex-basis 刻意等於上限：概述因此是一個剛性的 560 區塊，並排時就是 560、擠不下時
// 整塊換行後仍是 560，永遠 4 行。basis 取 480 的話 1280px 會分到 530px、掉成 5 行。
// 收縮係數保留 1 —— 手機上換行後只剩 358px 可用，不能收縮會直接撐破卡片。
const PROSE_MAX_WIDTH = 560;
const PROSE_MIN_WIDTH = PROSE_MAX_WIDTH;
// 清單比散文耐寬一些，但仍需要上限 —— 沒有它，1920px 下建議行動就是那個 824px 的例子
const LIST_MAX_WIDTH = 720;
const LIST_MIN_WIDTH = 420;

/**
 * 住客回饋摘要卡片。純讀取，不含任何摘要邏輯，也不觸發 AI。
 *
 * 明細入口無論有無摘要都存在 —— 排程掛掉時卡片退化成一份當期回饋清單，仍然有用。
 */
export default class FeedbackSummaryCard extends Component {
  // 明細的取數與呈現都在 FeedbackItemsDrawer，這張卡只負責摘要。
  // ids 為空代表整期明細，有值代表某一條亮點/問題的依據下鑽。
  state = { drawerOpen: false, drawerIds: null, drawerLabel: null };

  // 住客體驗卡的「查看客戶意見回饋」在另一張卡上，由 OpsDashboard 持 ref 呼叫這支。
  // label 給完整標題 (住客需求用)；沒給時沿用 topic 組出「依據回饋」的標題。
  openDrawer = (ids, topic, label) =>
    this.setState({
      drawerOpen: true,
      drawerIds: ids && ids.length ? ids : null,
      drawerLabel: label || (topic ? `「${topic}」的依據回饋` : null),
    });

  /**
   * 「依據的回饋」的入口。點下去把那幾則帶進明細抽屜。
   *
   * 原本是一個 maxHeight 320px 的捲動 popover。實測正式資料的 evidence 則數多數是
   * 1 到 6 則，但 2026-08 有一條 48 則、2026-07 月 41 則、季 30 則 —— 幾十則全文塞
   * 進浮層只能靠捲動，沒有分頁也沒有位置感，等於讀不到。抽屜本來就是左清單右全文
   * 配分頁，長清單交給它。
   */
  evidenceLink = (ids, topic) => {
    const { items } = this.props.data;
    const rows = (ids || []).filter((id) => items && items[String(id)]);
    if (!rows.length) return null;
    return (
      /* 用 Button 而非 <a>：沒有 href 的錨點不在 tab 順序裡，鍵盤使用者開不了它，
         而它是整張卡的信任錨點 (讓人驗證摘要不是模型編的)。
         style 把 Button 的內距與高度歸零，外觀維持成一段行內小字。 */
      <Button
        type="link"
        size="small"
        onClick={() => this.openDrawer(rows, topic)}
        style={{ fontSize: FONT.caption, marginLeft: 4, padding: 0, height: "auto" }}
      >
        [{rows.length} 則]
      </Button>
    );
  };

  renderEntries = (entries, kind) =>
    entries.map((e, i) => {
      const tag = kind === "issue" ? severityTag(e.severity) : { color: "green", label: "亮點" };
      return (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: i === entries.length - 1 ? 0 : 8 }}>
          <Tag color={tag.color} style={{ marginTop: 1, marginInlineEnd: 0, height: "fit-content" }}>{tag.label}</Tag>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Text strong>{e.topic}</Typography.Text>
            <span style={{ fontSize: FONT.caption, color: "#bfbfbf", marginLeft: 6 }}>{sourceLabel(e.sources)}</span>
            {this.evidenceLink(e.evidence, e.topic)}
            <div style={{ color: "#595959" }}>{e.detail}</div>
          </div>
        </div>
      );
    });

  render() {
    const { data, error } = this.props;
    const { drawerOpen, drawerIds, drawerLabel } = this.state;
    const status = cardState(data, error);
    const stale = data && isStale(data.generated_at);
    const hasActions = status === "ready" && (data.summary.actions || []).length > 0;

    return (
      <Card
        size="small"
        title="住客回饋摘要"
        style={{ marginTop: 16 }}
        extra={
          <Space size={8}>
            {data && data.item_count > 0 && (
              <span style={{ fontSize: FONT.caption, color: "#8c8c8c" }}>
                本期 {data.item_count} 則
                {data.summarized_count != null && data.summarized_count !== data.item_count
                  ? `・摘要涵蓋 ${data.summarized_count} 則` : ""}
              </span>
            )}
            {data && data.generated_at && (
              <span style={{ fontSize: FONT.caption, color: stale ? "#cf1322" : "#bfbfbf" }}>
                {stale ? "摘要逾 36 小時未更新・" : ""}
                {new Date(data.generated_at).toLocaleString("zh-TW", { hour12: false })} 產生
                {/* 看不出一份摘要是誰產生的，就無從比較三家的品質 ——
                    而能比較正是要多供應商的理由。舊資料沒有 provider，只顯示模型。 */}
                {data.model ? `・${data.provider ? `${data.provider} ` : ""}${data.model}` : ""}
              </span>
            )}
            {/* 這裡原本還有一顆「明細」按鈕，與上方住客體驗卡的「查看客戶意見回饋」
                是同一個去處。同一畫面兩個入口通往同一個抽屜只是雜訊，入口收斂到那顆；
                「AI 是加值而非前提」的後備由那顆在 error 態仍然顯示來守住。 */}
          </Space>
        }
      >
        {status === "loading" && <Spin />}
        {status === "error" && (
          <Alert type="error" showIcon message="讀取住客回饋失敗" description="請稍後重試；若持續發生請通知系統管理者。" />
        )}
        {status === "no-feedback" && <Empty description="本期尚無住客回饋" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        {status === "not-generated" && (
          <Empty description="尚未產生摘要，可先查看明細" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            {/* 包一層箭頭函式：直接傳 this.openDrawer 會把 click 事件當成 ids */}
            <Button size="small" onClick={() => this.openDrawer()}>檢視 {data.item_count} 則明細</Button>
          </Empty>
        )}
        {status === "ready" && (
          <>
            {/* 上排：左邊概述與分布、右邊建議行動。用 flex 而非 Row/Col —— 見上方
                PROSE_MAX_WIDTH 的註解，百分比欄會把概述從下面切穿。兩塊各自帶
                flex-basis 當下限，放不下時整塊換行堆疊，概述不會被壓成六行。 */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 24 }}>
              <div style={{ flex: `1 1 ${PROSE_MIN_WIDTH}px`, maxWidth: PROSE_MAX_WIDTH }}>
                <Typography.Paragraph>{data.summary.overview}</Typography.Paragraph>
                {data.summary.sentiment && (
                  /* 標籤與比例條原本是行內排列，欄位被壓到 414px 時放不下，實測把
                     「正面」留在上一行行尾、「71%・中性 29%・負面 0%」掉到下一行 ——
                     一個百分比被標點拆成兩行。改成可換行的 flex，並讓整段百分比
                     nowrap：要換就整塊換到下一行，不能從中間裂開。 */
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: FONT.caption, color: "#8c8c8c" }}>概估分布</span>
                    <Progress
                      percent={100}
                      showInfo={false}
                      size="small"
                      success={{ percent: data.summary.sentiment.positive }}
                      style={{ flex: "1 1 160px", minWidth: 120, maxWidth: 320 }}
                    />
                    <span style={{ fontSize: FONT.caption, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                      正面 {data.summary.sentiment.positive}%・中性 {data.summary.sentiment.neutral}%・負面 {data.summary.sentiment.negative}%
                    </span>
                  </div>
                )}
              </div>
              {hasActions && (
                /* 原本是 actions.join("；")。項目本身就會用到「；」(例如「統一計價規則；
                   並在通知住客前先確認金額」)，跟分隔用的分號撞在一起後，連項目邊界都
                   還原不了 —— 陣列已經有邊界，不該再壓成一句要人用標點拆回去。 */
                <div style={{ flex: `1 1 ${LIST_MIN_WIDTH}px`, maxWidth: LIST_MAX_WIDTH }}>
                  <div style={sectionHeaderStyle}>建議行動 {data.summary.actions.length}</div>
                  <ol style={{ margin: 0, paddingLeft: 20, color: "#595959" }}>
                    {data.summary.actions.map((action, i) => (
                      <li key={i} style={{ lineHeight: 1.7, marginBottom: i === data.summary.actions.length - 1 ? 0 : 4 }}>
                        {action}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
            {/* 下排沿用上排的同一組分界，不用 Col 的 50/50 —— 兩者混用時分界點會分岔：
                1920px 實測上排的界在 x=595、下排的「問題」在 x=852，同一張卡看起來像
                兩個對不齊的表格。這裡寬度規則與上排逐項相同，四個區塊因此共用一條界，
                也在同一個寬度一起換行。 */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 24, marginTop: 12 }}>
              <div style={{ flex: `1 1 ${PROSE_MIN_WIDTH}px`, maxWidth: PROSE_MAX_WIDTH }}>
                <div style={sectionHeaderStyle}>
                  亮點 {(data.summary.highlights || []).length}
                </div>
                {this.renderEntries(data.summary.highlights || [], "highlight")}
              </div>
              <div style={{ flex: `1 1 ${LIST_MIN_WIDTH}px`, maxWidth: LIST_MAX_WIDTH }}>
                <div style={sectionHeaderStyle}>
                  問題 {(data.summary.issues || []).length}
                </div>
                {this.renderEntries(data.summary.issues || [], "issue")}
              </div>
            </div>
          </>
        )}

        {/* 住客需求不受摘要狀態影響：它來自逐則標記的 SQL 彙總，不是 AI 摘要的一部分 */}
        {/* key 含期間：換期間時「查看全部」的展開狀態要收合回預設 */}
        <GuestNeedsSection
          key={`${this.props.from}|${this.props.to}`}
          data={this.props.needs}
          error={this.props.needsError}
          prevLabel={this.props.needsPrevLabel}
          onOpen={(ids, label) => this.openDrawer(ids, null, label)}
        />

        <FeedbackItemsDrawer
          open={drawerOpen}
          from={this.props.from}
          to={this.props.to}
          ids={drawerIds}
          label={drawerLabel}
          onClose={() => this.setState({ drawerOpen: false })}
          fetchItems={PmsAPI.ops.feedbackItems}
          lookupGuest={PmsAPI.ops.guestLookup}
          fetchGuestVisits={PmsAPI.ops.guestVisits}
        />
      </Card>
    );
  }
}
